// Dev-only fixtures + dispatcher for the OSS-verified lane (#68): the
// applicant's application on Billing, the staff queue on /admin/oss, and the
// verdict that closes it. Split out of `mocks.ts` the same way
// `billing-mocks.ts` is — one file per lane, each under the 150-LOC rule — and
// `mockFetch` delegates here identically: `null` means "not mine", so dispatch
// falls through.
//
// Stateful on purpose (like the `/v1/org/members` mock): a verdict rewrites the
// fixtures, because the real surfaces refetch after one instead of patching.
//
// `?mock_oss_none=1` empties the applicant's row so the apply FORM renders;
// without it Billing shows a pending application (same convention as
// `?mock_unpaid` / `?mock_signed_out`).

import type { Page } from './admin';
import type { AdminOssRequest } from './adminOss';
import { ApiError } from './api';
import type { OssVerificationRequest, OssVerificationStatus } from './ossVerification';

// Every date here sits on or before the marketing capture clock (`CAPTURE_NOW`,
// 2026-07-17T12:01Z, `apps/marketing/scripts/capture-dashboard.ts`): these
// render through `absolute()`, so a later one prints a submission from the
// future on a captured screen (#4838).
const at = (day: string): string => `${day}T12:00:00.000Z`;

let application: OssVerificationRequest = {
  id: 'ossv_mock_1',
  repo_full_name: 'developerz-ai/maintainer-agent',
  evidence:
    'Public repo under Apache-2.0 with 1.2k stars, listed by the OpenSSF as a supporting project. Maintained continuously since 2024 by three people.',
  status: 'pending',
  reviewer_note: null,
  submitted_at_iso: at('2026-07-15'),
  updated_at_iso: at('2026-07-15'),
  decided_at_iso: null,
};

let queue: readonly AdminOssRequest[] = [
  {
    id: 'ossv_mock_1',
    account_id: 'acc_devsample',
    account_login: 'developerz-ai',
    repo_full_name: application.repo_full_name,
    evidence: application.evidence,
    status: 'pending',
    reviewer_note: null,
    submitted_by_login: 'din',
    submitted_at_iso: at('2026-07-15'),
    decided_at_iso: null,
  },
  {
    id: 'ossv_mock_2',
    account_id: 'acc_meep',
    account_login: 'meepcastana',
    repo_full_name: 'meepcastana/tiny-parser',
    evidence: 'MIT licensed, 63 stars, active for 14 months.',
    status: 'approved',
    reviewer_note: 'Meets the stars + activity bar.',
    submitted_by_login: 'meepcastana',
    submitted_at_iso: at('2026-06-02'),
    decided_at_iso: at('2026-06-05'),
  },
];

/**
 * Rows matching the queue's `status` filter (`all` keeps every row).
 *
 * The filter is the shared grammar's `?filter=status:<value>`, matching the real
 * route — a mock still reading the retired flat `?status=` would serve the
 * pending queue for every tab and make a broken client look like a working one.
 */
function filtered(path: string): Page<AdminOssRequest> {
  const term = new URLSearchParams(path.split('?')[1] ?? '').get('filter');
  const status = term?.startsWith('status:') ? term.slice('status:'.length) : 'pending';
  const data = status === 'all' ? queue : queue.filter((r) => r.status === status);
  return { data, next_cursor: null };
}

/** The verdict body `lib/admin-overrides.ts` sends to the REST twin. */
type DecideBody = {
  readonly decision?: 'approve' | 'reject';
  readonly note?: string;
  readonly confirm?: unknown;
};

const DECIDE_PATH = /^\/v1\/admin\/oss-verifications\/([^/?]+)\/decide$/;

/** A problem+json refusal, as `apiFetch` throws it. */
const refused = (status: number, detail: string): Promise<never> =>
  Promise.reject(new ApiError(status, detail, undefined, detail));

/**
 * The staff verdict — `POST /v1/admin/oss-verifications/:id/decide`, the REST
 * twin of the retired MCP `oss_verify_decide` (plan 101 row 17-1).
 *
 * It refuses what the server refuses: a body without `confirm: true` (400), a
 * reject without a note (400), and an id that is not a PENDING application
 * (404 — unknown and already-decided are one answer). A mock that accepted
 * what the server refuses kept a client the server refused on every write green
 * (#5608 slice 17 row 24).
 *
 * The verdict MUTATES the fixtures, because both surfaces refetch rather than
 * patch: the queue's post-verdict reload shows the decided row under the
 * `approved`/`rejected` filter, and the applicant's own panel agrees.
 */
function decideMock(id: string, init?: RequestInit): Promise<unknown> {
  const body = (init?.body ? JSON.parse(String(init.body)) : {}) as DecideBody;
  if (body.confirm !== true) return refused(400, 'confirm: true is required');
  if (body.decision !== 'approve' && body.decision !== 'reject')
    return refused(400, 'decision must be approve or reject');
  if (body.decision === 'reject' && !body.note) return refused(400, 'a rejection needs a note');
  const row = queue.find((r) => r.id === id && r.status === 'pending');
  if (!row) return refused(404, 'no pending application with that id');

  const verdict = {
    status: (body.decision === 'approve' ? 'approved' : 'rejected') as OssVerificationStatus,
    reviewer_note: body.note ?? null,
    decided_at_iso: at('2026-07-17'),
  };
  queue = queue.map((r) => (r.id === id ? { ...r, ...verdict } : r));
  if (application.id === id)
    application = { ...application, ...verdict, updated_at_iso: verdict.decided_at_iso };

  return Promise.resolve({ request_id: id, status: verdict.status, account: null });
}

/** Serve the OSS-verification routes; `null` for anything else. */
export function ossMock(path: string, method: string, init?: RequestInit): Promise<unknown> | null {
  if (path === '/v1/oss/verification') {
    if (method === 'POST') return Promise.resolve({ request: application });
    const none = new URLSearchParams(window.location.search).has('mock_oss_none');
    return Promise.resolve({ request: none ? null : application });
  }

  if ((path.split('?')[0] ?? path) === '/v1/admin/oss-verifications')
    return Promise.resolve(filtered(path));

  const decide = method === 'POST' ? DECIDE_PATH.exec(path) : null;
  if (decide?.[1]) return decideMock(decodeURIComponent(decide[1]), init);

  return null;
}

/** Restore the pristine fixtures — the shared reset seam (`./mock-state.ts`). */
const PRISTINE_APPLICATION = structuredClone(application);
const PRISTINE_QUEUE = structuredClone(queue);

export function resetOssMocks(): void {
  application = structuredClone(PRISTINE_APPLICATION);
  queue = structuredClone(PRISTINE_QUEUE);
}
